import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Alert, Box, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import React from 'react';
import { Card, Loaded, SourceChip } from './parts';
import { FAILURE_CLASS_LABELS, STATUS_META, byStatusThenVolume, isFullSweep, isStale, latestFullSweep, providerStatus, showsProblem } from './model';
const duration = run => {
  const m = Math.round((Date.parse(run.finished_at) - Date.parse(run.started_at)) / 60000);
  if (!Number.isFinite(m)) return '-';
  return m >= 60 ? "".concat(Math.floor(m / 60), "h ").concat(m % 60, "m") : "".concat(m, "m");
};
const partialLabel = n => "Partial run (".concat(n, " ").concat(n === 1 ? 'provider' : 'providers', ")");
const NUMBERS = [['PDFs', 'pdfs'], ['New to S3', 's3_uploaded'], ['Stamped', 'stamped'], ['Quarantined', 'quarantined'], ['Dropped', 'dropped_unleased'], ['Unresolved', 'unresolved']];

/** Every run in the window, with canaries marked, and the last full sweep provider by provider. */
export const SweepsView = _ref => {
  let health = _ref.health;
  const sweep = latestFullSweep(health.rows);
  return /*#__PURE__*/React.createElement(Stack, {
    spacing: 16,
    sx: {
      marginTop: '16px'
    }
  }, /*#__PURE__*/React.createElement(Card, {
    title: "Runs",
    source: sweep ? /*#__PURE__*/React.createElement(SourceChip, {
      prod: true,
      at: sweep.finished_at
    }) : null
  }, /*#__PURE__*/React.createElement(Loaded, {
    state: health
  }, !health.rows.length ? /*#__PURE__*/React.createElement(Alert, {
    severity: "info"
  }, "The scraper has not published a run yet. It publishes one at the end of each sweep.") : /*#__PURE__*/React.createElement(React.Fragment, null, sweep && isStale(sweep.finished_at) ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    sx: {
      marginBottom: '12px'
    }
  }, "The last full sweep is more than 36 hours old. These numbers are not current.") : null, /*#__PURE__*/React.createElement(Box, {
    sx: {
      overflowX: 'auto'
    }
  }, /*#__PURE__*/React.createElement(Table, {
    size: "small"
  }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, null, "Finished"), /*#__PURE__*/React.createElement(TableCell, null, "Run"), /*#__PURE__*/React.createElement(TableCell, null, "Mode"), /*#__PURE__*/React.createElement(TableCell, null, "Duration"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }, "OK / swept"), NUMBERS.map(_ref2 => {
    let _ref3 = _slicedToArray(_ref2, 1),
      label = _ref3[0];
    return /*#__PURE__*/React.createElement(TableCell, {
      key: label,
      align: "right"
    }, label);
  }))), /*#__PURE__*/React.createElement(TableBody, null, health.rows.map((run, index) => {
    var _run$totals$providers, _run$totals, _run$totals$ok, _run$totals2, _run$totals$providers2, _run$totals3;
    const full = isFullSweep(run);
    return /*#__PURE__*/React.createElement(TableRow, {
      key: "".concat(run.finished_at, "-").concat(index),
      sx: full ? undefined : {
        '& td': {
          color: 'text.secondary'
        }
      }
    }, /*#__PURE__*/React.createElement(TableCell, {
      sx: {
        whiteSpace: 'nowrap'
      }
    }, new Date(run.finished_at).toLocaleString()), /*#__PURE__*/React.createElement(TableCell, null, /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      variant: full ? 'info' : 'inactive',
      label: full ? 'Full sweep' : partialLabel((_run$totals$providers = (_run$totals = run.totals) === null || _run$totals === void 0 ? void 0 : _run$totals.providers) !== null && _run$totals$providers !== void 0 ? _run$totals$providers : 0)
    })), /*#__PURE__*/React.createElement(TableCell, null, run.mode), /*#__PURE__*/React.createElement(TableCell, {
      sx: {
        whiteSpace: 'nowrap'
      }
    }, duration(run)), /*#__PURE__*/React.createElement(TableCell, {
      align: "right"
    }, "".concat((_run$totals$ok = (_run$totals2 = run.totals) === null || _run$totals2 === void 0 ? void 0 : _run$totals2.ok) !== null && _run$totals$ok !== void 0 ? _run$totals$ok : 0, " / ").concat((_run$totals$providers2 = (_run$totals3 = run.totals) === null || _run$totals3 === void 0 ? void 0 : _run$totals3.providers) !== null && _run$totals$providers2 !== void 0 ? _run$totals$providers2 : 0)), NUMBERS.map(_ref4 => {
      var _run$totals$key, _run$totals4;
      let _ref5 = _slicedToArray(_ref4, 2),
        label = _ref5[0],
        key = _ref5[1];
      return /*#__PURE__*/React.createElement(TableCell, {
        key: label,
        align: "right",
        sx: {
          fontVariantNumeric: 'tabular-nums'
        }
      }, ((_run$totals$key = (_run$totals4 = run.totals) === null || _run$totals4 === void 0 ? void 0 : _run$totals4[key]) !== null && _run$totals$key !== void 0 ? _run$totals$key : 0).toLocaleString());
    }));
  })))), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    sx: {
      display: 'block',
      marginTop: '8px'
    }
  }, "Fleet totals and the last sweep use full sweeps only. Partial runs are canaries and retries.")))), sweep ? /*#__PURE__*/React.createElement(Card, {
    title: "Last full sweep by provider"
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      overflowX: 'auto'
    }
  }, /*#__PURE__*/React.createElement(Table, {
    size: "small"
  }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, null, "Provider"), /*#__PURE__*/React.createElement(TableCell, null, "Status"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }, "Downloaded"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }, "Stamped"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }, "Quarantined"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }, "Logins"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }, "Not due"), /*#__PURE__*/React.createElement(TableCell, null, "Problem"))), /*#__PURE__*/React.createElement(TableBody, null, [...(sweep.providers || [])].sort(byStatusThenVolume).map(p => {
    var _p$errors;
    const _STATUS_META$provider = _slicedToArray(STATUS_META[providerStatus(p)], 2),
      label = _STATUS_META$provider[0],
      tone = _STATUS_META$provider[1];
    return /*#__PURE__*/React.createElement(TableRow, {
      key: p.key || p.provider
    }, /*#__PURE__*/React.createElement(TableCell, null, p.provider), /*#__PURE__*/React.createElement(TableCell, null, /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      variant: tone,
      label: label
    })), ['downloaded', 'stamped', 'quarantined', 'logins', 'skipped_not_due'].map(k => {
      var _p$k;
      return /*#__PURE__*/React.createElement(TableCell, {
        key: k,
        align: "right",
        sx: {
          fontVariantNumeric: 'tabular-nums'
        }
      }, (_p$k = p[k]) !== null && _p$k !== void 0 ? _p$k : 0);
    }), /*#__PURE__*/React.createElement(TableCell, null, showsProblem(p) ? /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, FAILURE_CLASS_LABELS[p.failure_class] || 'Unclassified', (_p$errors = p.errors) !== null && _p$errors !== void 0 && _p$errors[0] ? /*#__PURE__*/React.createElement(Typography, {
      component: "span",
      variant: "caption",
      color: "text.secondary"
    }, " \xB7 ".concat(p.errors[0])) : null) : null));
  }))))) : null);
};